import { html } from 'lit-element';
import { nothing } from 'lit-html';
import {
  KatalComponent,
  KatLitElement,
  booleanConverter,
} from '../../shared/base';
import { ifNotNull } from '../../utils/directives';

/**
 * @component {kat-accordion-header} KatalAccordionHeader The section of the Accordion that controls it's expanded state.
 * @event expand Fired when the body starts to open.
 * @event expanded Fired when the body finishing animating open.
 * @event collapse Fired when the body starts to close.
 * @event collapsed Fired when the body finishing animating close.
 */
export class KatalAccordionHeader extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {boolean} expanded Specifies if the accordion body is visible or not. Will be toggled immediately before animations have finished.
       */
      expanded: { converter: booleanConverter, reflect: true },

      /**
       * @classprop {string} label Specifies the label describing the content this header controls.
       */
      label: { type: String, reflect: true },

      /**
       * @classprop {string} kat-aria-label Defines the label read to screen reader users when they select the carousel.
       */
      katAriaLabel: {
        type: String,
        attribute: 'kat-aria-label',
        reflect: true,
      },

      /**
       * @classprop {string} badge-type Specifies the variant of badge displayed on the header.
       */
      badgeType: { type: String, attribute: 'badge-type' },

      /**
       * @classprop {string} badge-label Specifies the label for the badge dispalyed on the header.
       */
      badgeLabel: { type: String, attribute: 'badge-label' },

      /**
       * @classprop {boolean} remains-expanded If true this accordion will not auto collapse when a neighboring accordion expands.
       */
      remainsExpanded: {
        converter: booleanConverter,
        attribute: 'remains-expanded',
      },
    };
  }

  constructor() {
    super();
    this._preventAnimation = false;
    this.badgeType = 'default';
    this.expanded = false;
    this.badgeLabel = '';
  }

  createRenderRoot() {
    return this;
  }

  updated(changedProperties) {
    if (changedProperties.has('expanded')) {
      this.adjustSiblings();
    }
  }

  adjustSiblings() {
    const body = this.nextElementSibling;
    if (body === null || body.tagName !== 'KAT-ACCORDION-BODY') return;

    if (this.expanded) {
      this.getSiblings().forEach(sibling => {
        if (!sibling.remainsExpanded) {
          sibling.expanded = false;
        }
      });

      // Attempt to open children with one child
      const childAccordions = [].slice
        .call(body.children)
        .filter(child => child.tagName === 'KAT-ACCORDION');
      if (childAccordions.length === 1) {
        const header = childAccordions[0].querySelector('kat-accordion-header');
        if (header) {
          if (!header.expanded) {
            header._preventAnimation = true;
            header.expanded = true;
          }
        } else {
          throw 'kat-accordion must contain a kat-accordion-header element';
        }
      }

      this.performAnimation(body, true, 'expand', 'expanded');
    } else {
      this.performAnimation(body, false, 'collapse', 'collapsed');
    }
  }

  performAnimation(body, expanding, startEvent, endEvent) {
    if (this._preventAnimation) {
      this._preventAnimation = false;
      return;
    }

    const COLLAPSE_ANIMATION_TIME = 300;
    this.dispatchCustomEvent(startEvent);

    body.style.height = 'auto';
    body.classList.add('katal-collapsing');
    const height = body.getBoundingClientRect().height;
    const startHeight = expanding ? 0 : height;
    const endHeight = expanding ? height : 0;

    body.style.height = `${startHeight}px`;
    setTimeout(() => {
      // Must wait a certain amount of time for the browser to recognize the height being set above
      // so when height it set a second time it animates to the new value.
      body.style.height = `${endHeight}px`;
    }, 30);

    setTimeout(() => {
      body.classList.remove('katal-collapsing');
      body.removeAttribute('style');
      this.dispatchCustomEvent(endEvent);
    }, COLLAPSE_ANIMATION_TIME);
  }

  onClick(evt) {
    evt.stopPropagation();
    this.expanded = !this.expanded;
  }

  getSiblings() {
    // To find my parent's siblings
    const parent = this.parentNode.parentNode;
    return (
      [].slice
        .call(parent.children)
        .filter(element => element.tagName === 'KAT-ACCORDION')
        .filter(accordion => accordion !== this.parentNode)
        .map(accordion => accordion.querySelector('kat-accordion-header'))
        // Filter out the nulls
        .filter(headerOrNull => headerOrNull)
    );
  }

  render() {
    const badge = this.badgeLabel
      ? html`
          <kat-badge
            type=${this.badgeType}
            label=${this.badgeLabel}
          ></kat-badge>
        `
      : nothing;
    return html`
      <button
        type="button"
        @click=${this.onClick}
        aria-expanded=${this.expanded ? 'true' : 'false'}
        aria-label=${ifNotNull(this.katAriaLabel)}
      >
        <div class="kat-accordion-header-container">
          <h5 class="kat-accordion-header-text">${this.label}</h5>
          <span class="kat-accordion-badge-container"> ${badge} </span>
        </div>
        <div class="kat-accordion-toggle kat-accordion-arrow-right"></div>
      </button>
    `;
  }
}

KatalComponent.register('kat-accordion-header', KatalAccordionHeader);
